import { inject, singleton } from "tsyringe";

// adapter
import DateFns from "@/shared/libs/date/date-fns/DateFns";

import type {
    IDateAdapter,
    ParseISOAdapterOptions,
    FormatISOAdapterOptions,
    FormatDateAdapterOptions,
} from "@/shared/libs/date/interface";
import {
    EndOfMonthOptions,
    EndOfWeekOptions,
    StartOfMonthOptions,
    StartOfWeekOptions,
} from "date-fns";

@singleton()
export default class DateAdapter implements IDateAdapter {
    constructor(@inject(DateFns) private readonly dateFns: DateFns) {}

    public parseISO = (date: string, options?: ParseISOAdapterOptions): Date => {
        return this.dateFns.parseISO(date, options);
    };

    public formatISO = (date: Date, options?: FormatISOAdapterOptions): string => {
        return this.dateFns.formatISO(date, options);
    };

    public parseZoned = (date: Date, timeZone: string): Date => {
        return this.dateFns.parseZoned(date, timeZone);
    };

    public formatZoned = (
        date: Date,
        timeZone: string,
        formatStr?: string,
        options?: FormatDateAdapterOptions,
    ): string => {
        return this.dateFns.formatZoned(date, timeZone, formatStr, options);
    };

    public addWeeks = (date: Date, weeks: number): Date => {
        return this.dateFns.addWeeks(date, weeks);
    };

    public subWeeks = (date: Date, weeks: number): Date => {
        return this.dateFns.subWeeks(date, weeks);
    };

    public differenceInDays = (dateLeft: Date, dateRight: Date): number => {
        return this.dateFns.differenceInDays(dateLeft, dateRight);
    };

    public differenceInWeeks = (dateLeft: Date, dateRight: Date): number => {
        return this.dateFns.differenceInWeeks(dateLeft, dateRight);
    };

    public setHours = (
        date: Date,
        hours: number,
        minutes?: number,
        seconds?: number,
        milliseconds?: number,
    ): Date => {
        return this.dateFns.setHours(date, hours, minutes, seconds, milliseconds);
    };

    public formatQueryDate = (date: Date): string => {
        return this.dateFns.formatQueryDate(date);
    };

    /**
     * 날짜의 시간을 00:00:00으로 설정합니다.
     */
    public startOfDay = (date: Date, timeZone: string): Date => {
        return this.dateFns.startOfDay(date, timeZone);
    };

    /**
     * 두 날짜 사이의 일수 차이를 계산합니다. 시간 정보는 무시합니다.
     */
    public differenceInDaysIgnoringTime = (
        dateLeft: Date,
        dateRight: Date,
        timeZone: string,
    ): number => {
        return this.dateFns.differenceInDaysIgnoringTime(dateLeft, dateRight, timeZone);
    };

    /**
     * 24시간 이내의 시간 정보는 relative 로 표시되며, 그 이후는 yy.mm.dd 로 표시되도록 하는 함수입니다.
     * @param date target date
     * @param timezone target time zone
     * @returns {string} formatted date 입니다.
     */
    public formatRelativeDateIn24Hours = (date: Date, timezone: string): string => {
        return this.dateFns.formatRelativeDateIn24Hours(date, timezone);
    };

    /**
     * @description
     * Date 시간을 HH:mm 형식으로 변환하면서, 접두사로 오전, 오후가 표시됩니다. (한국 기준)
     * @param date target date
     * @param timezone target time zone
     * @returns {string} formatted date 입니다.
     */
    public formatTimeWithMeridiem = (date: Date, timezone?: string): string => {
        return this.dateFns.formatTimeWithMeridiem(date, timezone);
    };

    /**
     * @description
     * Date 시간을 yy.mm.dd HH:mm:ss 형식으로 반환합니다.
     * @param date target date
     * @param timezone target time zone
     * @returns {string} formatted date 입니다.
     */
    public formatFullYearDateAndTime = (date: Date, timezone?: string): string => {
        return this.dateFns.formatFullYearDateAndTime(date, timezone);
    };

    /**
     * 주어진 Month 내 첫번쨰 월요일을 반환합니다.
     */
    public firstMondayOfMonthRange = (date: Date, timezone?: string): Date => {
        return this.dateFns.firstMondayOfMonthRange(date, timezone);
    };

    /**
     * 주어진 Month 내 마지막 요일을 반환합니다.
     */
    public lastDayOfMonthRange = (date: Date, timezone?: string): Date => {
        return this.dateFns.lastDayOfMonthRange(date, timezone);
    };

    /**
     * 인자로 전달받은 시작날짜와 종료날짜 사이의 날짜들을 반환합니다.
     * @param startDate 시작날짜
     * @param endDate 종료날짜
     * @param timezone 시간대
     * @returns 시작날짜와 종료날짜 사이의 날짜들
     */
    public getDatesBetween = (startDate: Date, endDate: Date, timezone?: string): Date[] => {
        return this.dateFns.getDatesBetween(startDate, endDate, timezone);
    };

    /**
     * 같은 날짜인지 확인합니다
     */
    public isSameDate = (date1: Date, date2: Date, timezone?: string): boolean => {
        return this.dateFns.isSameDate(date1, date2, timezone);
    };

    /**
     * 같은 월인지 확인합니다
     */
    public isSameMonth = (date1: Date, date2: Date, timezone?: string): boolean => {
        return this.dateFns.isSameMonth(date1, date2, timezone);
    };

    /**
     * 오늘인지 확인합니다
     */
    public isToday = (date: Date, timezone?: string): boolean => {
        return this.dateFns.isToday(date, timezone);
    };

    /**
     * 주어진 날짜에서 월(months)만큼 뺍니다.
     */
    public subMonths = (date: Date, months: number, timezone?: string): Date => {
        return this.dateFns.subMonths(date, months, timezone);
    };

    /**
     * 주어진 날짜에서 월(months)만큼 더합니다.
     */
    public addMonths = (date: Date, months: number, timezone?: string): Date => {
        return this.dateFns.addMonths(date, months, timezone);
    };

    /**
     * 시작 주를 반환합니다.
     * @param date
     * @param options
     * @param timezone
     * @returns
     */
    public startOfWeek = (date: Date, options?: StartOfWeekOptions, timezone?: string): Date => {
        return this.dateFns.startOfWeek(date, options, timezone);
    };

    /**
     * 종료 주를 반환합니다.
     * @param date
     * @param options
     * @param timezone
     * @returns
     */
    public endOfWeek = (date: Date, options?: EndOfWeekOptions, timezone?: string): Date => {
        return this.dateFns.endOfWeek(date, options, timezone);
    };

    /**
     * 시작 월을 반환합니다.
     * @param date
     * @param timezone
     * @returns
     */
    public startOfMonth = (
        date: Date,
        options?: StartOfMonthOptions<Date>,
        timezone?: string,
    ): Date => {
        return this.dateFns.startOfMonth(date, options, timezone);
    };

    /**
     * 종료 월을 반환합니다.
     * @param date
     * @param timezone
     * @returns
     */
    public endOfMonth = (
        date: Date,
        options?: EndOfMonthOptions<Date>,
        timezone?: string,
    ): Date => {
        return this.dateFns.endOfMonth(date, options, timezone);
    };

    /**
     * 주어진 날짜에서 일(days)만큼 더합니다.
     * @param date
     * @param days
     * @param timezone
     * @returns
     */
    public addDays = (date: Date, days: number, timezone?: string): Date => {
        return this.dateFns.addDays(date, days, timezone);
    };
}
